/**
 * @author karolin.frennert@svenskaspel.se (Karolin Frennert)
 */

import Calendar from 'react-calendar';
import './../../stylesheets/date-picker.less';
import { default as Button } from 'trinidad-ui/button';
import { Form, ControlGroup, Inputv2 } from 'trinidad-ui/form';
import { default as Icon } from 'trinidad-ui/icon';
import { TimeInput } from 'trinidad-ui/time-input';
import { useState, useEffect, forwardRef, Fragment } from 'react';
import PropTypes from 'prop-types';

const DatePicker = forwardRef(({
  hasDisabledFormTag,
  enabledDatesArray,
  errorMessage,
  branding,
  className,
  hasButtons,
  hasError,
  hasRange,
  hasTime,
  externalInputValue,
  helpMessage,
  initialStartTime,
  initialEndTime,
  initialDate,
  infoMessage,
  inputName,
  inputHelpText,
  inputId,
  inputLabel,
  isDisabled,
  isOpen,
  popupPosition,
  minDate,
  onBlur,
  onSet,
  // eslint-disable-next-line
  onOpen,
  openWithButton,
  topComponent,
  ...props
}, ref) => {
  const [date, setDate] = useState(initialDate ? initialDate : null);
  const [inputValue, setInputValue] = useState(externalInputValue ? externalInputValue : '');
  const [displayCalendar, setDisplayCalendar] = useState(isOpen);
  const [startTime, setStartTime] = useState(initialStartTime ? initialStartTime : '00:00');
  const [endTime, setEndTime] = useState(initialEndTime ? initialEndTime : '00:00');
  const classNames = ['svs-date-picker'];

  /**
   * @description Check if current value can be a date
   * @param date string | number
   * @returns boolean
   */

  const checkDateIsValid = (date) => {
    if (typeof date === 'number') {
      return true;
    }
    // yyyy-mm-dd or yyyy-mm-ddT00:00:00+01:00
    const pattern = /^(?:\d{4}-\d{2}-\d{2})(?:T\d{2}:\d{2}:\d{2}\+\d{2}:\d{2})?$/;
    return pattern.test(date);
  };

  /**
   * @description Transforms date to a comparable value
   * @param date string | number
   * @returns string
   */

  const transformDate = (date) => {
    const newDate = new Date(date);
    return `${newDate.getFullYear()}${newDate.getMonth()}${newDate.getDate()}`;
  };

  /**
   * @description Determines wich dates should be enabled in the month view
   * @param enabledDatesArray Array
   * @param date string | number
   * @returns boolean
  */

  const enabledDates = (enabledDatesArray, date) => {
    const dateTime = transformDate(date);
    const activeDatesAsDates = enabledDatesArray.map((d) => {
      if (!checkDateIsValid(d)) {
        throw new Error('The date you passed is not in the right format, it needs to be yyyy-mm-dd');
      }
      return transformDate(d);
    });
    return !activeDatesAsDates.find((item) => item === dateTime);
  };

  if (className) {
    classNames.push(className);
  }

  /**
   * Handle submit, hide the calendar and call the onSet function
   * @param e {object} event object
   * @param data
   */
  const handleSubmit = (e, data) => {
    e?.preventDefault();
    onSet(data);
    setDisplayCalendar(false);
  };

  /**
   * Handle change, set the date and input value
   * @param data
   */
  const handleChange = (data) => {
    if (data) {
      if (data.length > 1) {
        const startDate = new Date(data[0].setHours(parseInt(startTime.split(':')[0], 10), startTime.split(':')[1], 0));
        const endDate = new Date(data[1].setHours(parseInt(endTime.split(':')[0], 10), endTime.split(':')[1], 0));
        data = [startDate, endDate];
        const inputVal = `${startDate.toLocaleDateString('sv-SE')}${hasTime ? `, ${startTime}` : ''}—${endDate.toLocaleDateString('sv-SE')}${hasTime ? `, ${endTime}` : ''}`;
        setInputValue(inputVal);
      } else {
        let inputVal;

        if (Array.isArray(data)) {
          inputVal = new Date(data[0].setHours(parseInt(startTime.split(':')[0], 10), startTime.split(':')[1], 0));
        } else {
          inputVal = new Date(data.setHours(parseInt(startTime.split(':')[0], 10), startTime.split(':')[1], 0));
        }
        data = [inputVal];
        setInputValue(`${inputVal.toLocaleDateString('sv-SE')}${hasTime ? `, ${startTime}` : ''}`);
      }
      setDate(data);
      if (!hasButtons && !hasTime && !hasRange) {
        handleSubmit(null, data);
      }
    }
  };
  /**
   * Handle onBlur function.
   * @param e {object} event object
   */
  const handleOnBlur = (e) => {
    if (onBlur) {
      onBlur(e);
    }
  };
  /**
   * Handle toggle of input field or openWithButton.
   * @param e {object} event object
   */

  const handleToggle = (e) => {
    if (displayCalendar) {
      handleSubmit(e, date);
    } else {
      if (onOpen) {
        onOpen(e);
      }
      setDisplayCalendar(true);
    }
  };

  /**
   * Render one or two time pickers based on if the calendar has a range
   * @param hasRange {boolean}
   * @returns {JSX.Element}
   */
  const renderTimePicker = (hasRange) => (
    <div className="svs-date-picker-time-holder">
      <TimeInput
        helpMessage={hasRange ? 'Ange starttid' : 'Tid'}
        inverted={branding === 'inverted'}
        label={hasRange ? 'Starttid' : 'Tid'}
        name="startTime"
        onChange={(e) => {
          setStartTime(e.target.value);
        }}
        value={startTime}
      />
      {hasRange && (
        <TimeInput
          helpMessage="Ange sluttid"
          inverted={branding === 'inverted'}
          label="Sluttid"
          name="endTime"
          onChange={(e) => {
            setEndTime(e.target.value);
          }}
          value={endTime}
        />
      )}
    </div>
  );

  const formatShortWeekday = (locale, date) => date.toLocaleDateString(locale, { weekday: 'short' })[0];

  /**
   * Render the calendar
   * @returns {JSX.Element}
   */
  const renderCalendar = () => (
    <div className={`svs-date-picker-${branding ? branding : 'neutral'} svs-date-picker-wrapper${popupPosition ? ` svs-date-picker-popup svs-date-picker-popup-${popupPosition} ` : ' '}elevation-100`}>
      {displayCalendar && topComponent && topComponent}
      <Calendar
        className={classNames.join(' ')}
        formatShortWeekday={formatShortWeekday}
        minDate={minDate}
        minDetail="decade"
        nextLabel={<Icon icon="menu-right" size="default" />}
        onChange={(data) => handleChange(data)}
        prevLabel={<Icon icon="menu-left" size="default" />}
        selectRange={Boolean(hasRange)}
        tileClassName="f-numeric"
        tileDisabled={({ date }) => (enabledDatesArray ? enabledDates(enabledDatesArray, date) : false)}
        value={date}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
      />
      {hasTime && renderTimePicker(hasRange)}
      { (hasButtons || hasTime || hasRange) && (
        <Button block className="svs-date-picker-button" inverted={branding === 'inverted'} onClick={(e) => handleSubmit(e, date)} type="submit">
          {`${date ? 'Spara' : 'Stäng'}`}
        </Button>
      )}
    </div>
  );
  /**
   * Render the input and calendar.
   * @returns {JSX.Element}
   */
  const renderInput = () => (
    <Fragment>
      {!openWithButton ?
(
  <ControlGroup errorMessage={errorMessage} hasError={hasError} helpMessage={helpMessage} infoMessage={infoMessage} v2>
    <Inputv2
      autoComplete="false"
      className="svs-date-picker-input"
      disabled={isDisabled}
      id={inputId}
      inputHelpText={inputHelpText ? inputHelpText : `${hasRange ? 'Välj period' : 'Välj dag'}`}
      inverted={branding === 'inverted'}
      label={inputLabel ? inputLabel : `${hasRange ? 'Välj period' : 'Välj dag'}`}
      name={`${inputName ? inputName : 'datePicker'}`}
      onBlur={(e) => handleOnBlur(e)}
      onClick={handleToggle}
      readOnly
      ref={ref}
      type="button"
      value={inputValue}
    />
  </ControlGroup>
    ) :
        (
          <Button block disabled={isDisabled} iconPosition="right" inverted onClick={handleToggle}>
            {' '}
            <Icon icon="menu-down" />
            {inputValue ? inputValue : `${hasRange ? 'Välj period' : 'Välj dag'}`}
          </Button>
)}
      {displayCalendar && renderCalendar()}
    </Fragment>
  );

  /**
   * Set the input value when the time changes
   */
  useEffect(() => {
    handleChange(date);
  }, [startTime, endTime, hasError]);

  useEffect(() => {
    if (externalInputValue !== undefined) {
      setInputValue(externalInputValue);
    }
  }, [externalInputValue]);

  useEffect(() => {
    setDisplayCalendar(isOpen);
  }, [isOpen]);

  return (
    <div className="svs-date-picker">
      { hasDisabledFormTag ?
        renderInput() :
        (
          <Form>
            {renderInput()}
          </Form>
        )}
    </div>
  );
});

DatePicker.propTypes = {
  /**
   * Custom branding.
   * Built in support for neutral, sport and tur.
   */
  branding: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.oneOf(['neutral', 'sport', 'tur'])
  ]),
  /**
   * Custom classes.
   */
  className: PropTypes.string,
  /**
   * Array with dates objects that should be the only available dates to pick. Should be in yyyy-mm-dd format.
   */
  enabledDatesArray: PropTypes.array,
  /**
   * Customized error message.
   */
  errorMessage: PropTypes.string,
  /**
   * Custom external input value.
   */
  externalInputValue: PropTypes.string,
  /**
   * If false, the calendar will close and run onSet Function on date selection.
   * If true, the calendar will stay open and run onSet Function on submit.
   * If calendar has time, the calendar will always have submit button and onSet function is called onSubmit.
   * If calendar has range, the calendar will always have submit button and onSet function is called onSubmit.
   */
  hasButtons: PropTypes.bool,
  /**
   * Disable the wrapping form tag.
   * If you use this prop you need to wrap the DatePicker in a svs Form tag.
   */
  hasDisabledFormTag: PropTypes.bool,
  /**
   * If component has an error state.
   */
  hasError: PropTypes.bool,
  /**
   * If calendar has range selection.
   */
  hasRange: PropTypes.bool,
  /**
   * If calendar has time selection.
   * If calendar has range and time, two time pickers will be rendered. One for start time and one for end time.
   */
  hasTime: PropTypes.bool,
  /**
   * Customized help message for input.
   */
  helpMessage: PropTypes.string,
  /**
   * Customized info message.
   */
  infoMessage: PropTypes.string,
  /**
   * Initial date for the calendar.
   * Defaults no date if not set.
   */
  initialDate: PropTypes.instanceOf(Date),
  /**
   * Initial ending time for the calendar.
   * Defaults to 00:00.
   */
  initialEndTime: PropTypes.string,
  /**
   * Initial startting time for the calendar.
   * Defaults to 00:00.
   */
  initialStartTime: PropTypes.string,
  /**
   * Help text for input label.
   */
  inputHelpText: PropTypes.string,
  /**
   * Custom id for the DateInput.
   */
  inputId: PropTypes.string,
  /**
   * Custom label for the input field.
   * Defaults to either 'Välj period' if the datePicker has a range
   * or 'Välj dag' if its a singular input.
   */
  inputLabel: PropTypes.string,
  /**
   * Custom name for the DateInput.
   * Default dateInput.
   */
  inputName: PropTypes.string,
  /**
   * Disables the input.
   * Default is false.
   */
  isDisabled: PropTypes.bool,
  /**
   * Controls the visibility of the calendar.
   */
  isOpen: PropTypes.bool,
  /**
   * Sets the minimum date that the calendar will display.
   * Default to 1/1 2019.
   */
  minDate: PropTypes.instanceOf(Date),
  /**
   * onBlur function for the input.
   */
  onBlur: PropTypes.func,
  /**
   * Callback function when date is set, returns array with date objects.
   * One date object if calendar has no range.
   * Two date objects if calendar has range.
   */
  onSet: PropTypes.func.isRequired,
  /**
   * If the calendar should open with a button. Default is false.
   */
  openWithButton: PropTypes.bool,
  /**
   * If set calendar should appear on top of other elements. And is aligned to the left, center or right.
   */
  popupPosition: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.oneOf(['left', 'center', 'right'])
  ]),
  /**
   * Custom component to render above the calendar.
   * If custom component needs to handle isOpen prop, pass a function to onOpen prop to be able to control the calendar.
   */
  topComponent: PropTypes.element
};

DatePicker.defaultProps = {
  branding: 'neutral',
  hasButtons: false,
  hasDisabledFormTag: false,
  hasError: false,
  hasRange: false,
  hasTime: false,
  isDisabled: false,
  isOpen: false,
  minDate: new Date(2019, 0, 1),
  // eslint-disable-next-line
  onOpen: () => {},
  openWithButton: false,
  popupPosition: ''
};

export default DatePicker;
